/**
 * Templates List Component
 *
 * Displays a list of notification templates for a merchant.
 * Allows filtering by event, channel, status and creating/editing templates.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { AppCard } from "../../components/ui";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { confirmDialog } from "primereact/confirmdialog";
import { Pencil, Trash2, Ban, CheckCircle } from "lucide-react";
import Loader from "../../components/Loader";
import { storageService } from "../../services/StorageService";
import { operatorsApiService } from "../../services/api/users/OperatorApiService";
import {
    templatesApiService,
    eventDefinitionsApiService,
} from "../../services/api/notifications";
import {
    TemplateListItemDto,
    EventDefinitionDto,
    NotificationChannel,
    NotificationChannelLabels,
    TemplateStatus,
    TemplateStatusLabels,
    getChannelOptions,
    getStatusOptions,
} from "../../models/notifications";
import TemplateEditor from "./TemplateEditor";

interface TemplatesProps {
    merchantId?: number;
    initialEventCode?: string;
}

const Templates: React.FC<TemplatesProps> = ({ merchantId: propMerchantId, initialEventCode }) => {
    const toast = useRef<Toast>(null);

    const merchantId = propMerchantId ?? Number(storageService.getSelectedMerchantId());

    const [isLoading, setIsLoading] = useState(true);
    const [templates, setTemplates] = useState<TemplateListItemDto[]>([]);
    const [events, setEvents] = useState<EventDefinitionDto[]>([]);
    const [userEmail, setUserEmail] = useState<string>("");

    // Filters
    const [filterEventCode, setFilterEventCode] = useState<string | undefined>(initialEventCode);
    const [filterChannel, setFilterChannel] = useState<NotificationChannel | undefined>();
    const [filterStatus, setFilterStatus] = useState<TemplateStatus | undefined>();

    // Pagination
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);

    // Editor
    const [editorVisible, setEditorVisible] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

    useEffect(() => {
        loadEvents();
        loadUserData();
    }, []);

    const loadUserData = async () => {
        const result = await operatorsApiService.get();
        if (result.success) {
            setUserEmail(result.data.email);
        }
    };

    useEffect(() => {
        setFirst(0); // Reset to first page when filters change
        loadTemplates();
    }, [merchantId, filterEventCode, filterChannel, filterStatus]);

    const loadEvents = async () => {
        const result = await eventDefinitionsApiService.getActive(merchantId);
        if (result.success) {
            setEvents(result.data);
        }
    };

    const loadTemplates = async () => {
        setIsLoading(true);
        const result = await templatesApiService.search({
            merchantId,
            eventCode: filterEventCode,
            channel: filterChannel,
            status: filterStatus,
        });
        if (result.success) {
            setTemplates(result.data);
        } else {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: result.message || "Failed to load templates",
                life: 4000,
            });
        }
        setIsLoading(false);
    };

    // Client-side filtering (fallback if backend doesn't filter)
    const filteredTemplates = useMemo(() => {
        return templates.filter((t) => {
            if (filterEventCode && t.eventCode !== filterEventCode) return false;
            if (filterChannel !== undefined && t.channel !== filterChannel) return false;
            if (filterStatus !== undefined && t.status !== filterStatus) return false;
            return true;
        });
    }, [templates, filterEventCode, filterChannel, filterStatus]);

    const resetFilters = () => {
        setFilterEventCode(undefined);
        setFilterChannel(undefined);
        setFilterStatus(undefined);
    };

    const openCreate = () => {
        setEditingTemplateId(null);
        setEditorVisible(true);
    };

    const openEdit = (template: TemplateListItemDto) => {
        setEditingTemplateId(template.templateId);
        setEditorVisible(true);
    };

    const handleEditorSave = () => {
        setEditorVisible(false);
        setEditingTemplateId(null);
        loadTemplates();
    };

    const handleDelete = (template: TemplateListItemDto) => {
        confirmDialog({
            message: `Are you sure you want to delete this template for "${template.eventCode}"?`,
            header: "Delete Template",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                const result = await templatesApiService.delete(merchantId, template.templateId, userEmail || storageService.getUserData()?.email || "portal-user");
                if (result.success) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Deleted",
                        detail: "Template has been deleted",
                        life: 3000,
                    });
                    loadTemplates();
                } else {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: result.message || "Failed to delete template",
                        life: 4000,
                    });
                }
            },
        });
    };

    const handleToggleStatus = async (template: TemplateListItemDto) => {
        const newStatus = template.status === TemplateStatus.Active
            ? TemplateStatus.Disabled
            : TemplateStatus.Active;

        const result = await templatesApiService.updateStatus(
            merchantId,
            template.templateId,
            newStatus,
            userEmail
        );

        if (result.success) {
            toast.current?.show({
                severity: "success",
                summary: "Updated",
                detail: `Template status changed to ${TemplateStatusLabels[newStatus]}`,
                life: 3000,
            });
            loadTemplates();
        } else {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: result.message || "Failed to update template status",
                life: 4000,
            });
        }
    };

    // Column templates
    const eventCodeTemplate = (row: TemplateListItemDto) => (
        <span className="font-mono font-semibold text-blue-600">{row.eventCode}</span>
    );

    const channelTemplate = (row: TemplateListItemDto) => (
        <Tag value={NotificationChannelLabels[row.channel]} severity="info" />
    );

    const statusTemplate = (row: TemplateListItemDto) => {
        const severity = row.status === TemplateStatus.Active
            ? "success"
            : row.status === TemplateStatus.Disabled
            ? "danger"
            : "warning";
        return <Tag value={TemplateStatusLabels[row.status]} severity={severity} />;
    };

    const localizationsTemplate = (row: TemplateListItemDto) => (
        <Tag
            value={`${row.localizationCount} language${row.localizationCount !== 1 ? "s" : ""}`}
            className="bg-slate-100 text-slate-700"
        />
    );

    const createdAtTemplate = (row: TemplateListItemDto) => (
        <span className="text-sm text-gray-600">
            {new Date(row.createdAt).toLocaleDateString()}
        </span>
    );

    const actionsTemplate = (row: TemplateListItemDto) => (
        <div className="flex items-center gap-1">
            <button
                type="button"
                onClick={() => openEdit(row)}
                className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                title="Edit"
            >
                <Pencil size={15} />
            </button>
            <button
                type="button"
                onClick={() => handleToggleStatus(row)}
                className={`p-1.5 rounded-md transition-colors ${row.status === TemplateStatus.Active
                    ? "text-neutral-400 hover:text-warning-600 hover:bg-warning-50 dark:hover:text-warning-400 dark:hover:bg-warning-900/20"
                    : "text-neutral-400 hover:text-success-600 hover:bg-success-50 dark:hover:text-success-400 dark:hover:bg-success-900/20"
                }`}
                title={row.status === TemplateStatus.Active ? "Disable" : "Activate"}
            >
                {row.status === TemplateStatus.Active ? <Ban size={15} /> : <CheckCircle size={15} />}
            </button>
            <button
                type="button"
                onClick={() => handleDelete(row)}
                className="p-1.5 rounded-md text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:text-danger-400 dark:hover:bg-danger-900/20 transition-colors"
                title="Delete"
            >
                <Trash2 size={15} />
            </button>
        </div>
    );

    // Filter options
    const eventOptions = events.map((e) => ({ label: e.eventCode, value: e.eventCode }));
    const channelOptions = getChannelOptions();
    const statusOptions = getStatusOptions();

    const hasFilters = !!filterEventCode || filterChannel !== undefined || filterStatus !== undefined;

    const header = (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                    <Dropdown
                        value={filterEventCode}
                        options={eventOptions}
                        onChange={(e) => setFilterEventCode(e.value ?? undefined)}
                        placeholder="Filter by Event"
                        className="w-48"
                        showClear
                    />
                    <Dropdown
                        value={filterChannel}
                        options={channelOptions}
                        onChange={(e) => setFilterChannel(e.value ?? undefined)}
                        placeholder="Filter by Channel"
                        className="w-40"
                        showClear
                    />
                    <Dropdown
                        value={filterStatus}
                        options={statusOptions}
                        onChange={(e) => setFilterStatus(e.value ?? undefined)}
                        placeholder="Filter by Status"
                        className="w-40"
                        showClear
                    />
                    {hasFilters && (
                        <Button
                            icon="pi pi-filter-slash"
                            label="Clear"
                            outlined
                            size="small"
                            onClick={resetFilters}
                        />
                    )}
                </div>
                <Button
                    size="small"
                    label="Create Template"
                    icon="pi pi-plus"
                    onClick={openCreate}
                />
            </div>
        </div>
    );

    if (isLoading && templates.length === 0) {
        return <Loader />;
    }

    return (
        <>
            <Toast ref={toast} position="bottom-center" />

            <AppCard>
                <AppCard.Header>
                    <div className="flex flex-col gap-1">
                        <span>Notification Templates</span>
                        <small className="text-neutral-500 font-normal">
                            Manage notification templates for this merchant. Templates define the content sent for each event and channel.
                        </small>
                    </div>
                </AppCard.Header>
                <AppCard.Body>
                    <DataTable
                        value={filteredTemplates}
                        loading={isLoading}
                        header={header}
                        emptyMessage="No templates found. Create one to get started."
                        paginator
                        first={first}
                        rows={rows}
                        onPage={(e) => {
                            setFirst(e.first);
                            setRows(e.rows);
                        }}
                        rowsPerPageOptions={[10, 20, 50]}
                        stripedRows
                        pt={{
                            header: { className: "border-gray-200 rounded-t-lg" },
                            wrapper: { className: "border-gray-200 border-x border-b" },
                            bodyRow: { className: "even:!bg-blue-50/50" },
                        }}
                    >
                        <Column
                            field="eventCode"
                            header="Event"
                            body={eventCodeTemplate}
                            sortable
                            style={{ minWidth: "180px" }}
                        />
                        <Column
                            header="Channel"
                            body={channelTemplate}
                            style={{ width: "120px" }}
                        />
                        <Column
                            header="Status"
                            body={statusTemplate}
                            style={{ width: "100px" }}
                        />
                        <Column
                            header="Languages"
                            body={localizationsTemplate}
                            style={{ width: "120px" }}
                        />
                        <Column
                            header="Created"
                            body={createdAtTemplate}
                            style={{ width: "100px" }}
                        />
                        <Column
                            header="Actions"
                            body={actionsTemplate}
                            style={{ width: "150px" }}
                        />
                    </DataTable>
                </AppCard.Body>
            </AppCard>

            {editorVisible && (
                <TemplateEditor
                    merchantId={merchantId}
                    templateId={editingTemplateId}
                    initialEventCode={filterEventCode}
                    onSave={handleEditorSave}
                    onCancel={() => {
                        setEditorVisible(false);
                        setEditingTemplateId(null);
                    }}
                />
            )}
        </>
    );
};

export default Templates;
